import React, { useState, useEffect, useCallback, createContext, useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { useTranslation } from "react-i18next";
import { 
  Sparkles, 
  ArrowRight, 
  FileText, 
  Plus, 
  BarChart3, 
  Upload, 
  Download, 
  Bell, 
  Settings,
  Clock,
  Zap,
  TrendingUp,
  UserPlus,
  FolderPlus,
  ArrowRightLeft,
  AlertTriangle,
  FileSpreadsheet,
  X,
  ChevronRight,
  Lightbulb
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import axios from "axios";

const API = import.meta.env.VITE_API_URL || import.meta.env.REACT_APP_BACKEND_URL || "";

// Icon mapping
const iconMap = {
  FileText, Plus, BarChart3, Upload, Download, Bell, Settings, Clock, Zap,
  TrendingUp, UserPlus, FolderPlus, ArrowRightLeft, AlertTriangle, FileSpreadsheet
};

// Smart Learning Context
const SmartLearningContext = createContext(null);

export const useSmartLearning = () => useContext(SmartLearningContext);

// Provider component
export const SmartLearningProvider = ({ children }) => {
  const { token, user } = useAuth();
  const location = useLocation();
  const [suggestions, setSuggestions] = useState([]);
  const [quickActions, setQuickActions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Track page visits
  const trackAction = useCallback(async (actionType, feature = null, context = null) => {
    if (!token || !user) return;
    
    try {
      const page = location.pathname.replace("/", "") || "dashboard";
      await axios.post(`${API}/api/smart/track?user_id=${user.id}`, {
        action_type: actionType,
        page: page,
        feature: feature,
        context: context
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (err) {
      console.debug("Smart tracking error:", err);
    }
  }, [token, user, location.pathname]);

  // Fetch suggestions for current page
  const fetchSuggestions = useCallback(async () => {
    if (!token || !user) return;
    
    setIsLoading(true);
    try {
      const page = location.pathname.replace("/", "") || "dashboard";
      const res = await axios.get(`${API}/api/smart/suggestions/${page}?user_id=${user.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSuggestions(res.data.predictions || []);
      setQuickActions(res.data.quick_actions || []);
    } catch (err) {
      console.debug("Smart suggestions error:", err);
    } finally {
      setIsLoading(false);
    }
  }, [token, user, location.pathname]);

  // Track page visits automatically
  useEffect(() => {
    if (token && user) {
      trackAction("page_visit");
      fetchSuggestions();
    }
  }, [location.pathname, token, user]);

  const value = {
    suggestions,
    quickActions,
    isLoading,
    trackAction,
    fetchSuggestions
  };

  return (
    <SmartLearningContext.Provider value={value}>
      {children}
    </SmartLearningContext.Provider>
  );
};

// Smart Suggestions Bar Component - Shows at top of pages
export const SmartSuggestionsBar = ({ className }) => {
  const { suggestions, quickActions, trackAction } = useSmartLearning() || {};
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(true);
  const [dismissed, setDismissed] = useState([]);

  if (!suggestions?.length && !quickActions?.length) return null;
  if (!isVisible) return null;

  const handleAction = (action) => {
    trackAction?.("feature_use", action.action || action.type);
    if (action.path || action.action_path) {
      navigate(action.path || action.action_path);
    }
  };

  const dismissSuggestion = (id) => {
    setDismissed(prev => [...prev, id]);
  };

  const visibleSuggestions = suggestions?.filter(s => !dismissed.includes(s.id)) || [];
  const topPrediction = visibleSuggestions.find(s => s.type === "prediction");

  return (
    <div className={cn("mb-4 animate-fadeIn", className)}>
      {/* Prediction Banner */}
      {topPrediction && topPrediction.confidence > 0.6 && (
        <Card className="mb-3 border-indigo-200 bg-gradient-to-r from-indigo-50 to-purple-50">
          <CardContent className="py-3 px-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center">
                  <Lightbulb className="text-indigo-600" size={16} />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-800">{topPrediction.title}</p>
                  <p className="text-xs text-slate-500">{topPrediction.description}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-xs bg-white">
                  {Math.round(topPrediction.confidence * 100)}% säker
                </Badge>
                <Button 
                  size="sm" 
                  className="h-8 bg-indigo-600 hover:bg-indigo-700"
                  onClick={() => handleAction(topPrediction)}
                >
                  Ja, gör det
                  <ArrowRight size={14} className="ml-1" />
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm"
                  className="h-8 w-8 p-0"
                  onClick={() => dismissSuggestion(topPrediction.id)}
                >
                  <X size={14} />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Actions Row */}
      {quickActions?.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-slate-500 font-medium flex items-center gap-1">
            <Zap size={12} className="text-amber-500" />
            Snabbåtgärder:
          </span>
          {quickActions.slice(0, 4).map((action, idx) => {
            const IconComponent = iconMap[action.icon] || Plus;
            return (
              <Button
                key={idx}
                variant="outline"
                size="sm"
                className="h-7 text-xs gap-1 hover:bg-indigo-50 hover:border-indigo-300 hover:text-indigo-700"
                onClick={() => handleAction(action)}
              >
                <IconComponent size={12} />
                {action.title}
              </Button>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Floating Smart Assistant Button
export const SmartAssistantButton = () => {
  const { suggestions } = useSmartLearning() || {};
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  
  const hasSuggestions = suggestions?.length > 0;

  if (!hasSuggestions) return null;

  return (
    <div className="fixed bottom-6 right-6 z-50">
      {isOpen && (
        <Card className="absolute bottom-14 right-0 w-72 shadow-xl animate-in slide-in-from-bottom-2">
          <CardContent className="p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium flex items-center gap-1">
                <Sparkles size={14} className="text-amber-500" />
                Smarta förslag
              </span>
              <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setIsOpen(false)}>
                <X size={12} />
              </Button>
            </div>
            <div className="space-y-2">
              {suggestions.slice(0, 3).map((suggestion, idx) => (
                <button
                  key={idx}
                  className="w-full text-left p-2 rounded-lg hover:bg-slate-100 transition-colors"
                  onClick={() => {
                    navigate(suggestion.action_path);
                    setIsOpen(false);
                  }}
                >
                  <p className="text-sm font-medium text-slate-800">{suggestion.title}</p>
                  {suggestion.description && (
                    <p className="text-xs text-slate-500 mt-0.5">{suggestion.description}</p>
                  )}
                  {suggestion.confidence > 0 && (
                    <Badge variant="outline" className="mt-1 text-xs">
                      {Math.round(suggestion.confidence * 100)}% säker
                    </Badge>
                  )}
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
      
      <Button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "h-12 w-12 rounded-full shadow-lg transition-all",
          isOpen 
            ? "bg-slate-700 hover:bg-slate-800" 
            : "bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600"
        )}
      >
        {isOpen ? <X size={20} /> : <Sparkles size={20} />}
        {!isOpen && hasSuggestions && (
          <span className="absolute -top-1 -right-1 w-4 h-4 bg-amber-500 rounded-full text-[10px] flex items-center justify-center text-white font-bold">
            {suggestions.length}
          </span>
        )}
      </Button>
    </div>
  );
};

// Personalized Dashboard Section
export const PersonalizedQuickActions = ({ className }) => {
  const { quickActions, trackAction } = useSmartLearning() || {};
  const navigate = useNavigate();
  const { t } = useTranslation();

  if (!quickActions?.length) return null;

  return (
    <Card className={cn("", className)}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
            <Zap size={16} className="text-amber-500" />
            Dina snabbåtgärder
          </h3>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {quickActions.slice(0, 4).map((action, idx) => {
            const IconComponent = iconMap[action.icon] || Plus;
            return (
              <button
                key={idx}
                onClick={() => {
                  trackAction?.("feature_use", action.action);
                  navigate(action.path);
                }}
                className="flex flex-col items-center gap-2 p-3 rounded-lg border border-slate-200 hover:border-indigo-300 hover:bg-indigo-50 transition-all group"
              >
                <div className="w-10 h-10 rounded-full bg-slate-100 group-hover:bg-indigo-100 flex items-center justify-center transition-colors">
                  <IconComponent size={20} className="text-slate-600 group-hover:text-indigo-600" />
                </div>
                <span className="text-xs font-medium text-slate-700 group-hover:text-indigo-700 text-center">
                  {action.title}
                </span>
              </button>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default SmartLearningProvider;
